/**
 * Ticket (fare family / branded fare) summaries for the "Upgrade your ticket"
 * section and the fares modal.
 *
 * WHY TEXT, NOT ICONS
 * -------------------
 * The old box reduced every amenity to six booleans (meal / trolley / suitcase /
 * seat / change / cancel) by keyword-matching the supplier's description, then
 * drew an icon per boolean. The keywords do not match every airline's wording,
 * so tickets showed ✕ for services they include. This module keeps the
 * supplier's own list — `fare.additional_services` on each segment (Amadeus
 * `fareDetailsBySegment[].amenities`) — and only de-duplicates it.
 *
 * Cockpit sends a fare-family NAME (LITE / FLEX) but no service list, so a
 * summary may legitimately have a name and no services.
 */

export interface FareService {
  /** Upper-cased description — the translation key the B2C already uses. */
  key: string;
  description: string;
  amenityType?: string;
  /** true = included in the ticket; false = offered for a fee. */
  included: boolean;
}

export interface LegFare {
  /** "TLV → JFK" */
  route: string;
  names: string[];
}

export interface FareSummary {
  /** Distinct ticket names across the whole offer, in travel order. */
  names: string[];
  /** Per-leg names — only meaningful when `isMixed`. */
  legs: LegFare[];
  /** Different ticket names on different legs / segments. */
  isMixed: boolean;
  services: FareService[];
  /** Comparable identity of the ticket choice, e.g. "LIGHT|LIGHT|CLASSIC". */
  signature: string;
}

const legsOf = (offer: any): any[][] => {
  if (!offer) return [];
  if (Array.isArray(offer.legs) && offer.legs.length > 1) return offer.legs;
  const from = offer.flightFrom ?? offer.flight;
  return [from, offer.flightBack]
    .map((leg) => (Array.isArray(leg) ? leg : leg ? [leg] : []))
    .filter((leg) => leg.length > 0);
};

const unique = <T>(values: T[]): T[] => Array.from(new Set(values));

export const summarizeOfferFare = (offer: any): FareSummary => {
  const legs = legsOf(offer);
  const segments = legs.flat();

  const segmentNames = segments.map((segment: any) =>
    typeof segment?.fare?.name === 'string' ? segment.fare.name.trim() : ''
  );
  const names = unique(segmentNames.filter(Boolean));

  const legFares: LegFare[] = legs.map((leg) => ({
    route: `${leg[0]?.from?.code ?? ''} → ${leg[leg.length - 1]?.to?.code ?? ''}`,
    names: unique(
      leg
        .map((segment: any) => segment?.fare?.name)
        .filter((name: any) => typeof name === 'string' && name.trim())
        .map((name: string) => name.trim())
    ),
  }));

  // De-duplicate by description; a service counts as included only if every
  // segment that lists it includes it (a fee on any leg is a fee).
  const byKey = new Map<string, FareService>();
  segments.forEach((segment: any) => {
    const list = Array.isArray(segment?.fare?.additional_services)
      ? segment.fare.additional_services
      : [];
    list.forEach((amenity: any) => {
      const description =
        typeof amenity?.description === 'string' ? amenity.description.trim() : '';
      if (!description) return;
      const key = description.toUpperCase();
      const included = amenity?.isChargeable === false;
      const existing = byKey.get(key);
      if (existing) {
        existing.included = existing.included && included;
      } else {
        byKey.set(key, {
          key,
          description,
          amenityType: amenity?.amenityType,
          included,
        });
      }
    });
  });
  const all = Array.from(byKey.values());
  const services = [
    ...all.filter((service) => service.included),
    ...all.filter((service) => !service.included),
  ];

  return {
    names,
    legs: legFares,
    isMixed: names.length > 1,
    services,
    signature: segmentNames.join('|'),
  };
};

/**
 * Sentence-case a supplier description ("CHANGE BEFORE DEPARTURE" →
 * "Change before departure") when no translation exists for it. Units and
 * codes such as "23KG" keep their capitals.
 */
export const prettifyServiceDescription = (description: string): string => {
  const lower = description.trim().toLowerCase();
  const sentence = lower.charAt(0).toUpperCase() + lower.slice(1);
  return sentence.replace(/\b(\d+)\s?kg\b/gi, '$1kg');
};
